/**
 * Vendor slider controller.
 *
 * Keeps tab, panel, dot, track and autoplay state scoped to each section and
 * survives Shopify Theme Editor section load/unload cycles.
 */
(function() {
  'use strict';

  var MANAGER_KEY = 'DrhVendorSlider';
  var ROOT_SELECTOR = '[data-vendor-slider]';

  function rootsIn(scope) {
    if (!scope || typeof scope.querySelectorAll !== 'function') return [];

    var roots = [];
    if (scope.matches && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    var descendants = scope.querySelectorAll(ROOT_SELECTOR);
    for (var i = 0; i < descendants.length; i++) roots.push(descendants[i]);
    return roots;
  }

  function createController(section) {
    if (section.__drhVendorSliderController) return section.__drhVendorSliderController;

    var tablist = section.querySelector('[data-vendor-tabs]');
    var tabs = Array.prototype.slice.call(section.querySelectorAll('[data-vendor-tab]'));
    var track = section.querySelector('[data-vendor-track]');
    var prevBtn = section.querySelector('[data-vendor-prev]');
    var nextBtn = section.querySelector('[data-vendor-next]');
    var mainArea = section.querySelector('[data-vendor-slides]');
    var totalSlides = tabs.length;

    if (!tablist || !track || totalSlides === 0) return null;

    var currentIndex = 0;
    var autoplayInterval = null;
    var resizeTimer = null;
    var focusTimer = null;
    var observer = null;
    var isIntersecting = true;
    var isPointerInside = false;
    var destroyed = false;
    var listeners = [];
    var autoslideDesktop = section.dataset.autoslideDesktop === 'true';
    var autoslideMobile = section.dataset.autoslideMobile === 'true';
    var autoslideSeconds = parseInt(section.dataset.autoslideInterval, 10) || 5;
    var autoslideMs = autoslideSeconds * 1000;
    var motionQuery = typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;

    function listen(target, type, handler, options) {
      if (!target) return;
      target.addEventListener(type, handler, options);
      listeners.push(function() {
        target.removeEventListener(type, handler, options);
      });
    }

    function normalizeIndex(index) {
      var parsed = parseInt(index, 10);
      if (Number.isNaN(parsed)) return currentIndex;
      return ((parsed % totalSlides) + totalSlides) % totalSlides;
    }

    function panelAt(index) {
      return section.querySelector('[data-vendor-panel="' + index + '"]');
    }

    function panels() {
      return Array.prototype.slice.call(section.querySelectorAll('[data-vendor-panel]'));
    }

    function dots() {
      return Array.prototype.slice.call(section.querySelectorAll('[data-vendor-dot]'));
    }

    function ensureSlideInDom(index) {
      if (index < 0 || index >= totalSlides) return null;
      var panel = panelAt(index);
      if (!panel) return null;

      var template = panel.querySelector('template[data-vendor-template="' + index + '"]');
      if (template) {
        var content = template.content.cloneNode(true);
        template.replaceWith(content);
      }

      panel.setAttribute('data-vendor-hydrated', 'true');
      return panel;
    }

    function syncTabs(index) {
      tabs.forEach(function(tab, tabIndex) {
        var isActive = tabIndex === index;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });
    }

    function syncDots(index) {
      dots().forEach(function(dot) {
        var isActive = parseInt(dot.getAttribute('data-vendor-dot'), 10) === index;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    function handOffPanelFocus(previousPanel, targetPanel, index, activeElement) {
      if (!previousPanel || previousPanel === targetPanel || !previousPanel.contains(activeElement)) return;

      var focusTarget = null;
      var sourceDot = activeElement.closest && activeElement.closest('[data-vendor-dot]');
      if (sourceDot) {
        var dotIndex = sourceDot.getAttribute('data-vendor-dot');
        focusTarget = targetPanel.querySelector('[data-vendor-dot="' + dotIndex + '"]');
      }
      if (!focusTarget) focusTarget = tabs[index];
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    }

    function shouldAutoplay() {
      var enabled = window.innerWidth >= 768 ? autoslideDesktop : autoslideMobile;
      return enabled && !(motionQuery && motionQuery.matches) && isIntersecting && !isPointerInside && !section.contains(document.activeElement);
    }

    function startAutoplay() {
      if (destroyed || autoplayInterval || !shouldAutoplay() || totalSlides <= 1) return;
      autoplayInterval = window.setInterval(function() {
        goToSlide(currentIndex + 1);
      }, autoslideMs);
    }

    function stopAutoplay() {
      if (!autoplayInterval) return;
      window.clearInterval(autoplayInterval);
      autoplayInterval = null;
    }

    function goToSlide(requestedIndex, options) {
      if (destroyed || totalSlides === 0) return;

      var settings = options || {};
      var index = normalizeIndex(requestedIndex);
      var previousPanel = panelAt(currentIndex);
      var activeElement = document.activeElement;
      var targetPanel = ensureSlideInDom(index);
      if (!targetPanel) return;

      /* The target must be available before focus leaves the old panel. */
      targetPanel.setAttribute('aria-hidden', 'false');
      targetPanel.removeAttribute('inert');
      currentIndex = index;

      syncTabs(index);
      syncDots(index);
      if (motionQuery && motionQuery.matches) {
        track.style.transition = 'none';
      } else {
        track.style.removeProperty('transition');
      }
      track.style.transform = 'translateX(-' + (index * 100) + '%)';
      handOffPanelFocus(previousPanel, targetPanel, index, activeElement);

      /* Only after focus handoff may every offscreen panel become inert. */
      panels().forEach(function(panel) {
        if (panel === targetPanel) return;
        panel.setAttribute('aria-hidden', 'true');
        panel.setAttribute('inert', '');
      });

      if (autoplayInterval && settings.resetAutoplay !== false) {
        stopAutoplay();
        startAutoplay();
      }
    }

    function eventElement(event) {
      return event.target && event.target.closest ? event.target : null;
    }

    function handleClick(event) {
      var target = eventElement(event);
      if (!target) return;

      var tab = target.closest('[data-vendor-tab]');
      if (tab && section.contains(tab)) {
        goToSlide(tab.getAttribute('data-vendor-tab'));
        return;
      }

      var dot = target.closest('[data-vendor-dot]');
      if (dot && section.contains(dot)) {
        goToSlide(dot.getAttribute('data-vendor-dot'));
        return;
      }

      if (prevBtn && (target === prevBtn || prevBtn.contains(target))) {
        goToSlide(currentIndex - 1);
        return;
      }

      if (nextBtn && (target === nextBtn || nextBtn.contains(target))) {
        goToSlide(currentIndex + 1);
      }
    }

    function handleTabKeydown(event) {
      var target = eventElement(event);
      var tab = target && target.closest('[data-vendor-tab]');
      if (!tab || !tablist.contains(tab)) return;

      var index = tabs.indexOf(tab);
      var targetIndex = null;
      if (event.key === 'ArrowLeft') {
        targetIndex = normalizeIndex(index - 1);
      } else if (event.key === 'ArrowRight') {
        targetIndex = normalizeIndex(index + 1);
      } else if (event.key === 'Home') {
        targetIndex = 0;
      } else if (event.key === 'End') {
        targetIndex = totalSlides - 1;
      }

      if (targetIndex === null) return;
      event.preventDefault();
      goToSlide(targetIndex);
      tabs[targetIndex].focus({ preventScroll: true });
    }

    function handleMouseEnter() {
      isPointerInside = true;
      stopAutoplay();
    }

    function handleMouseLeave() {
      isPointerInside = false;
      if (shouldAutoplay()) startAutoplay();
    }

    function handleFocusOut() {
      window.clearTimeout(focusTimer);
      focusTimer = window.setTimeout(function() {
        if (shouldAutoplay()) startAutoplay();
      }, 0);
    }

    listen(section, 'mouseenter', handleMouseEnter);
    listen(section, 'mouseleave', handleMouseLeave);
    listen(section, 'focusin', stopAutoplay);
    listen(section, 'focusout', handleFocusOut);
    listen(section, 'click', handleClick);
    listen(tablist, 'keydown', handleTabKeydown);

    var touchStartX = 0;
    function handleTouchStart(event) {
      touchStartX = event.changedTouches[0].screenX;
      stopAutoplay();
    }

    function handleTouchEnd(event) {
      var diff = touchStartX - event.changedTouches[0].screenX;
      if (Math.abs(diff) > 50) {
        goToSlide(currentIndex + (diff > 0 ? 1 : -1));
      }
      if (shouldAutoplay()) startAutoplay();
    }

    listen(mainArea, 'touchstart', handleTouchStart, { passive: true });
    listen(mainArea, 'touchend', handleTouchEnd, { passive: true });

    function handleMotionPreferenceChange() {
      stopAutoplay();
      goToSlide(currentIndex, { resetAutoplay: false });
      if (shouldAutoplay()) startAutoplay();
    }
    if (motionQuery) listen(motionQuery, 'change', handleMotionPreferenceChange);

    function handleResize() {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function() {
        track.style.transform = 'translateX(-' + (currentIndex * 100) + '%)';
      }, 100);
    }
    listen(window, 'resize', handleResize);

    if ('IntersectionObserver' in window) {
      observer = new window.IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          isIntersecting = entry.isIntersecting;
          if (entry.isIntersecting) {
            if (shouldAutoplay()) startAutoplay();
          } else {
            stopAutoplay();
          }
        });
      }, { threshold: 0.3 });
      observer.observe(section);
    }

    function destroy() {
      if (destroyed) return;
      destroyed = true;
      stopAutoplay();
      window.clearTimeout(resizeTimer);
      window.clearTimeout(focusTimer);
      if (observer) observer.disconnect();
      listeners.splice(0).forEach(function(removeListener) {
        removeListener();
      });
      delete section.__drhVendorSliderController;
      delete section.dataset.vendorSliderInitialized;
    }

    var controller = {
      destroy: destroy,
      goToSlide: goToSlide
    };
    section.__drhVendorSliderController = controller;
    section.dataset.vendorSliderInitialized = 'true';

    goToSlide(0, { resetAutoplay: false });
    startAutoplay();
    return controller;
  }

  function initScope(scope) {
    rootsIn(scope || document).forEach(function(section) {
      createController(section);
    });
  }

  function destroyScope(scope) {
    rootsIn(scope || document).forEach(function(section) {
      var controller = section.__drhVendorSliderController;
      if (controller) controller.destroy();
    });
  }

  if (window[MANAGER_KEY]) {
    window[MANAGER_KEY].initScope(document);
    return;
  }

  var manager = {
    initScope: initScope,
    destroyScope: destroyScope
  };
  window[MANAGER_KEY] = manager;

  document.addEventListener('shopify:section:load', function(event) {
    manager.initScope(event.target);
  });
  document.addEventListener('shopify:section:unload', function(event) {
    manager.destroyScope(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      manager.initScope(document);
    }, { once: true });
  } else {
    manager.initScope(document);
  }
})();
